import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Typography,
  Box,
} from "@mui/material";
import { Warning, DeleteOutline, InfoOutlined } from "@mui/icons-material";
import { Button } from "./Button";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm: () => void;
  onCancel: () => void;
  variant?: "danger" | "warning" | "info";
  loading?: boolean;
}

/**
 * Composant de dialogue de confirmation stylisé
 * Remplace les window.confirm() par une modal Material-UI
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmText = "Confirmer",
  cancelText = "Annuler",
  onConfirm,
  onCancel,
  variant = "info",
  loading = false,
}) => {
  const getVariantStyles = () => {
    switch (variant) {
      case "danger":
        return {
          icon: <DeleteOutline sx={{ fontSize: 28, color: "error.main" }} />,
          iconColor: "error.main",
          confirmButtonVariant: "danger" as const,
        };
      case "warning":
        return {
          icon: <Warning sx={{ fontSize: 28, color: "warning.main" }} />,
          iconColor: "warning.main",
          confirmButtonVariant: "primary" as const,
        };
      default:
        return {
          icon: <InfoOutlined sx={{ fontSize: 28, color: "info.main" }} />,
          iconColor: "info.main",
          confirmButtonVariant: "primary" as const,
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
        },
      }}
    >
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {styles.icon}
          <Typography variant="h3" sx={{ fontSize: "20px", fontWeight: 700 }}>
            {title}
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <DialogContentText
          sx={{ color: "text.primary", fontSize: "14px", mt: 1 }}
        >
          {message}
        </DialogContentText>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, gap: 2 }}>
        <Button variant="text" onClick={onCancel} disabled={loading}>
          {cancelText}
        </Button>
        <Button
          variant={styles.confirmButtonVariant}
          onClick={onConfirm}
          loading={loading}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
